// ============================================================
// SideQuests - ErrorBoundary
// Catches render/lifecycle errors anywhere below it and shows a
// recoverable UI instead of an unmounted blank screen.
// ============================================================

import React, { Component, ErrorInfo, ReactNode } from 'react';
import Glyph from './Glyphs';

interface ErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, message: error?.message || 'Something went wrong.' };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[SQ] ErrorBoundary caught:', error, info);
    this.props.onError?.(error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, message: '' });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="section" style={{ textAlign: 'center', paddingTop: 80, paddingBottom: 80 }}>
          <div style={{ marginBottom: 12, color: 'var(--text-muted)' }}><Glyph name="compass" size={48} /></div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>
            Something went wrong
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, maxWidth: 300, marginLeft: 'auto', marginRight: 'auto' }}>
            {this.state.message}
          </p>
          <button className="btn btn-primary" onClick={this.handleReload}>
            <Glyph name="refresh" size={16} /> Reload SideQuests
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
